import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { novelsApi } from '../../services/api';

// Async thunks
export const fetchNovels = createAsyncThunk(
    'novels/fetchAll',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await novelsApi.getAll(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch novels');
        }
    }
);

export const createNovel = createAsyncThunk(
    'novels/create',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await novelsApi.create(formData);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create novel');
        }
    }
);

export const updateNovel = createAsyncThunk(
    'novels/update',
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await novelsApi.update(id, formData);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update novel');
        }
    }
);

export const patchNovel = createAsyncThunk(
    'novels/patch',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            await novelsApi.patch(id, data);
            return { id: Number(id), ...data };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update novel');
        }
    }
);

export const deleteNovel = createAsyncThunk(
    'novels/delete',
    async (id, { rejectWithValue }) => {
        try {
            await novelsApi.delete(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete novel');
        }
    }
);

export const updateNovelOrder = createAsyncThunk(
    'novels/updateOrder',
    async ({ categoryId, order }, { rejectWithValue }) => {
        try {
            await novelsApi.updateOrder(categoryId, order);
            return { categoryId, order };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update order');
        }
    }
);

export const bulkUploadNovels = createAsyncThunk(
    'novels/bulkUpload',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await novelsApi.bulkUpload(formData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Bulk upload failed');
        }
    }
);

const initialState = {
    items: [],
    loading: false,
    error: null,
    pagination: null,
};

const novelsSlice = createSlice({
    name: 'novels',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        reorderNovels: (state, action) => {
            state.items = action.payload;
        },
        toggleField: (state, action) => {
            const { id, field } = action.payload;
            const item = state.items.find(i => i.id === id);
            if (item) {
                item[field] = !item[field];
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNovels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNovels.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.data || [];
                state.pagination = action.payload.pagination || null;
            })
            .addCase(fetchNovels.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(createNovel.pending, (state) => {
                state.loading = true;
            })
            .addCase(createNovel.fulfilled, (state, action) => {
                state.loading = false;
                state.items.unshift(action.payload);
            })
            .addCase(createNovel.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(updateNovel.fulfilled, (state, action) => {
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = { ...state.items[index], ...action.payload };
                }
            })
           .addCase(patchNovel.fulfilled, (state, action) => {
                const index = state.items.findIndex(item => Number(item.id) === Number(action.payload.id));
                if (index !== -1) {
                    state.items[index] = { ...state.items[index], ...action.payload };
                }
            })
            .addCase(deleteNovel.fulfilled, (state, action) => {
                state.items = state.items.filter(item => item.id !== action.payload);
            });
    },
});

export const { clearError, reorderNovels, toggleField } = novelsSlice.actions;
export default novelsSlice.reducer;
